import { MouseEvent, Dispatch, SetStateAction, useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import classNames from 'classnames';
import { Constants } from '@praktio/common';
import { Spinner } from 'react-bootstrap';

// Ducks
import { RootState } from '../../../ducks/configureStore';
import {
  setSelected,
  update as updateQuiz,
  setDisplayedEdit,
} from '../../../ducks/quizLitmosReducer';

// Components
import CATAChoice from '../../SlideAssets/CATAChoice';
import CATAChoiceExplanationPanel from '../../SlideAssets/CATAChoiceExplanationPanel';
import FeedbackBar from '../../InteractivePanel/FeedbackBar/FeedbackBar';
import InteractiveDocument from '../../SlideAssets/InteractiveDocument';
import { SupplementalDocStates, WindowSizes } from '../../../utils/constants';
import { cataSlideHasDocumentEdits } from '../../../utils/slide';

// Styles
import './CATA.scss';
const { AnnotationTypes, CataTypes, ResourceItemTypes, ResourceTypes } = Constants;

interface InteractiveDocumentContentIProps {
  activityComplete?: boolean;
  expandedExplanations?: string[];
  choices?: Annotation[];
  displayedEditId?: string;
  hasLandscapeDoc: boolean;
  hasPortraitDoc: boolean;
  interactiveDocument?: Annotation[];
  isImageDoc?: boolean;
  layoutIsHorizontal: boolean;
  newlyExpandedExplanation: boolean;
  panelIds?: string[];
  slideSubType?: string;
  windowSize: WindowSizes;
  supplementalDoc?: SupplementalDoc;
  supplementalDocState?: SupplementalDocStates;
  setNewlyExpandedExplanation: Dispatch<SetStateAction<boolean>>;
  setSupplementalDocState?: Dispatch<SetStateAction<SupplementalDocStates>>;
  onComputedMaxWidth?: (maxWidth: number | undefined) => void;
}
const InteractiveDocumentContent = ({
  activityComplete,
  expandedExplanations,
  choices,
  displayedEditId,
  hasLandscapeDoc,
  hasPortraitDoc,
  interactiveDocument,
  isImageDoc,
  layoutIsHorizontal,
  newlyExpandedExplanation,
  panelIds,
  slideSubType,
  windowSize,
  supplementalDoc,
  supplementalDocState,
  setNewlyExpandedExplanation,
  setSupplementalDocState,
  onComputedMaxWidth,
}: InteractiveDocumentContentIProps) => {
  if (!interactiveDocument) {
    return null;
  }

  if (slideSubType === CataTypes.EditHorizontal) {
    const shouldShowExplanationPanel =
      !!activityComplete || windowSize !== WindowSizes.Small || !isImageDoc;

    return (
      <>
        {shouldShowExplanationPanel ? (
          <CATAChoiceExplanationPanel
            choices={choices}
            displayedEditId={displayedEditId}
            layoutIsHorizontal={layoutIsHorizontal}
            completed={!!activityComplete}
            expandedExplanations={expandedExplanations}
          />
        ) : null}

        <InteractiveDocument
          panelIds={panelIds}
          layoutIsHorizontal={layoutIsHorizontal}
          hasLandscapeDoc={hasLandscapeDoc}
          hasPortraitDoc={hasPortraitDoc}
          windowSize={windowSize}
          supplementalDoc={supplementalDoc}
          supplementalDocState={supplementalDocState}
          setSupplementalDocState={setSupplementalDocState}
          choices={choices}
          displayedEditId={displayedEditId}
          onComputedMaxWidth={onComputedMaxWidth}
        />
      </>
    );
  }

  return (
    <InteractiveDocument
      panelIds={panelIds}
      layoutIsHorizontal={layoutIsHorizontal}
      hasLandscapeDoc={hasLandscapeDoc}
      hasPortraitDoc={hasPortraitDoc}
      newlyExpandedExplanation={newlyExpandedExplanation}
      setNewlyExpandedExplanation={setNewlyExpandedExplanation}
      windowSize={windowSize}
      supplementalDoc={supplementalDoc}
      supplementalDocState={supplementalDocState}
      setSupplementalDocState={setSupplementalDocState}
      choices={choices}
      displayedEditId={displayedEditId}
      onComputedMaxWidth={onComputedMaxWidth}
    />
  );
};

const returnExpandedExplanations = ({
  choices,
  selected,
}: {
  choices: Annotation[];
  selected: string[];
}): string[] => {
  const lastSelectedChoiceId = selected[selected.length - 1] || '';

  return [lastSelectedChoiceId];
};

const setExpandedExplanationsAndDisplayedEdit = ({
  choices,
  displayedEditId,
  expandedExplanationsRef,
  layoutIsHorizontal,
  selected,
  dispatch,
  setExpandedExplanations,
}: {
  choices: Annotation[];
  displayedEditId?: string;
  expandedExplanationsRef: React.MutableRefObject<string[]>;
  layoutIsHorizontal: boolean;
  selected: string[];
  dispatch: Dispatch<any>;
  setExpandedExplanations: Dispatch<SetStateAction<string[]>>;
}) => {
  let newExpandedExplanations = returnExpandedExplanations({
    choices,
    selected,
  });

  let newDisplayedEditId: string | undefined = newExpandedExplanations[0];

  if (selected.length < 1) {
    setExpandedExplanations(newExpandedExplanations);
    dispatch(setDisplayedEdit(''));

    return;
  }

  if (newExpandedExplanations) {
    expandedExplanationsRef.current = newExpandedExplanations;
    setExpandedExplanations(newExpandedExplanations);
  }

  if (newDisplayedEditId && newDisplayedEditId !== displayedEditId) {
    dispatch(setDisplayedEdit(newDisplayedEditId));
  }
};

interface IProps {
  panelIds?: string[];
  layoutIsHorizontal: boolean;
  hasLandscapeDoc: boolean;
  hasPortraitDoc: boolean;
  windowSize: WindowSizes;
  supplementalDoc?: SupplementalDoc;
  supplementalDocState?: SupplementalDocStates;
  setSupplementalDocState?: React.Dispatch<React.SetStateAction<SupplementalDocStates>>;
}

const CATA = ({
  panelIds,
  layoutIsHorizontal,
  hasLandscapeDoc,
  hasPortraitDoc,
  windowSize,
  supplementalDoc,
  supplementalDocState,
  setSupplementalDocState,
}: IProps) => {
  const dispatch = useDispatch();

  // REDUX STATE
  const {
    quizLitmos,
    outOfTries,
    selected,
    slideTypeIsSingleSelect,
    choices,
    interactiveDocument,
    isBestEdit,
    displayedEditId,
    activityComplete,
    slideMainType,
    slideSubType,
    slideId,
    hasAnswerBase,
    timeline,
    hidePreviewCaret,
    layout,
  } = useSelector((state: RootState) => {
    const { slidesLitmos, coursesLitmos, quizLitmos, learner } = state;
    const { currentSlide } = slidesLitmos;
    const { outOfTries, displayedEditId } = quizLitmos;
    const selected = quizLitmos.selected as string[];
    const {
      timeline,
      isSingle: slideTypeIsSingleSelect,
      subType: slideSubType,
      learnerData,
      type: slideMainType,
      _id: slideId,
      hidePreviewCaret,
      layout,
    } = currentSlide || {};
    const { completed: activityComplete } = learnerData || {};

    // Get panel Id's
    // TODO - Further customize order when LMS options are in place.
    const documentPanelId = panelIds && panelIds.length > 2 ? panelIds[2] : null;
    const choicesPanelId = panelIds && panelIds.length > 1 ? panelIds[1] : null;

    const isBestEdit = [CataTypes.EditHorizontal, CataTypes.EditVertical].includes(
      slideSubType || '',
    );

    const filteredChoicesTimeline = timeline?.filter(
      (resource) => resource.panelId === choicesPanelId,
    );
    const filteredDocumentTimeline = timeline?.filter(
      (resource) => resource.panelId === documentPanelId,
    );

    const choices = filteredChoicesTimeline
      ? filteredChoicesTimeline.filter((resource) => resource.type === ResourceItemTypes.Answer)
      : [];

    const interactiveDocument = filteredDocumentTimeline
      ? filteredDocumentTimeline.filter(
          (resource) =>
            resource.type === ResourceItemTypes.Document ||
            resource.type === ResourceItemTypes.Text ||
            resource.type === ResourceTypes.Image,
        )
      : [];

    const arrayOfAnswerBases = timeline?.filter(
      (timelineElement) => timelineElement.subType === AnnotationTypes.AnswerBase,
    );
    const hasAnswerBase = !!(arrayOfAnswerBases && arrayOfAnswerBases.length > 0);

    return {
      activityComplete,
      choices,
      coursesLitmos,
      displayedEditId,
      hasAnswerBase,
      interactiveDocument,
      isBestEdit,
      learner,
      outOfTries,
      quizLitmos,
      selected,
      slideId,
      slideMainType,
      slidesLitmos,
      slideSubType,
      slideTypeIsSingleSelect,
      timeline,
      hidePreviewCaret,
      layout,
    };
  });

  // State
  const [changeAnnouncement, setChangeAnnouncement] = useState('');
  const [documentMaxWidth, setDocumentMaxWidth] = useState<number | undefined>();
  const [expandedChoices, setExpandedChoices] = useState<string[]>([]);
  const [expandedExplanations, setExpandedExplanations] = useState<string[]>([]);
  const [newlyExpandedExplanation, setNewlyExpandedExplanation] = useState(false);

  // Refs
  const cataRef = useRef<HTMLDivElement>(null);
  const choicesWrapperRef = useRef<HTMLDivElement>(null);
  const expandedExplanationsRef = useRef(expandedExplanations);
  const prevActivityCompleteStatusRef = useRef(activityComplete);
  const prevSlideId = useRef<string | undefined>();

  // Derived values
  const isImageDoc = interactiveDocument?.some((doc) => doc.type === ResourceTypes.Image);
  const hasChoiceImages = choices?.some((c) => c.imageUrl);
  const willComputeMaxWidth = hasLandscapeDoc && !!hasChoiceImages && !!isImageDoc;
  const isWaitingForMaxWidth = willComputeMaxWidth && !documentMaxWidth;

  const handleCaretClick = (choiceId: string) => {
    if (expandedChoices.includes(choiceId)) {
      const newArray = expandedChoices.filter((choice) => choice !== choiceId);
      setExpandedChoices(newArray);
    } else {
      setExpandedChoices([...expandedChoices, choiceId]);
    }
  };

  const handleChoiceClick = (
    event: MouseEvent,
    setEyeIconVisibility: Dispatch<SetStateAction<boolean>>,
  ): void => {
    event.preventDefault();

    // Do nothing if out of tries or the Supplemental Doc is fully open.
    if (
      outOfTries ||
      (supplementalDocState && supplementalDocState === SupplementalDocStates.FullyOpen)
    ) {
      return;
    }

    if (event.currentTarget instanceof HTMLElement) {
      // Disable tooltip/pulsing.
      dispatch(
        updateQuiz({
          ...quizLitmos,
          tooltipEngaged: false,
        }),
      );

      const selectionId = event.currentTarget.dataset.id || '';
      const indexOfSelection = selected.indexOf(selectionId);

      // If this choice was already in the array of selected choices, remove it.
      if (indexOfSelection > -1) {
        if (displayedEditId === selectionId) {
          setEyeIconVisibility(false);
          dispatch(setDisplayedEdit(''));
        }
        const newSelection = selected.filter((item) => item !== selectionId);
        dispatch(setSelected(newSelection));
        // If this choice was NOT already in the array of selected choices...

        // Display edits for the last selected choice.
        const lastSelectedChoice = newSelection[newSelection.length - 1] || '';
        dispatch(setDisplayedEdit(lastSelectedChoice));

        setChangeAnnouncement(''); // Clear the screen reader announcement.
      } else {
        setEyeIconVisibility(true);
        // Set the array of choices to this choice (single select)
        if (slideTypeIsSingleSelect) {
          dispatch(setSelected([selectionId]));
          // Add this choice to the array of choices (multi select).
        } else {
          dispatch(setSelected([...selected, selectionId]));
        }
        dispatch(setDisplayedEdit(selectionId));

        const selectedChoice = event.currentTarget.getAttribute('aria-label') || ''; // E.g. "Option A"

        setChangeAnnouncement(
          `Edits have been made based on your selection: ${selectedChoice}. Navigate to the document to review.`,
        );
      }
    }
  };

  const handleEyeClick = (event: MouseEvent, id: string, choiceDisabled?: boolean): void => {
    event.preventDefault();
    event.stopPropagation();
    if (choiceDisabled) {
      return;
    }
    if (displayedEditId === id) {
      dispatch(setDisplayedEdit(''));
    } else {
      dispatch(setDisplayedEdit(id));
    }
  };

  const handleExplanationButtonClick = (event: MouseEvent, id: string): void => {
    // TODO - These have been left for future reminders. They have been
    // deactivated to allow clicking them to disable the pulsing, but
    // want them to be a reminder if behavior starts acting up.
    // REMOVE BEFORE FINAL MERGE
    // event.preventDefault();
    // event.stopPropagation();

    dispatch(
      updateQuiz({
        ...quizLitmos,
        tooltipEngaged: false,
      }),
    );

    const indexOfId = expandedExplanations.indexOf(id);

    if (indexOfId === -1) {
      // If this explanation is not displayed and the activity is completed OR the slide type is CATAEditHorizontal,
      // set the displayededitid to this id.
      if (!!activityComplete || slideSubType === CataTypes.EditHorizontal) {
        dispatch(setDisplayedEdit(id));
      }

      setExpandedExplanations([id]);
    } else if (slideSubType !== CataTypes.EditHorizontal) {
      const newArrayOfExpandedExplanations = expandedExplanations.filter(
        (expandedExplanation) => expandedExplanation !== id,
      );

      // If no expanded explanations, set the displayedEdit to remove preview.
      if (newArrayOfExpandedExplanations.length === 0) {
        dispatch(setDisplayedEdit(''));
      }

      setExpandedExplanations(newArrayOfExpandedExplanations);
    }
  };

  // Clear expanded explanations when changing slides or completing the activity.
  useEffect(() => {
    if (
      prevSlideId.current !== slideId ||
      (!prevActivityCompleteStatusRef.current && activityComplete)
    ) {
      setExpandedExplanationsAndDisplayedEdit({
        choices,
        selected,
        layoutIsHorizontal,
        displayedEditId,
        expandedExplanationsRef,
        setExpandedExplanations,
        dispatch,
      });
    }
    prevActivityCompleteStatusRef.current = activityComplete;
    prevSlideId.current = slideId;
  }, [slideId, activityComplete, dispatch, choices, displayedEditId, layoutIsHorizontal, selected]);

  // Set newlyExpandedExplanation to true when an explanation becomes expanded (for autoscroll behavior).
  // This value is set back to false after autoscrolling in TextLoader.tsx.
  useEffect(() => {
    if (expandedExplanations.length > expandedExplanationsRef.current?.length || 0) {
      setNewlyExpandedExplanation(true);
    }
    expandedExplanationsRef.current = expandedExplanations;
  }, [expandedExplanations, slideSubType]);

  // Propagate content max-width to the Slide ancestor so the InstructionalPanel can match.
  useEffect(() => {
    const slideEl = cataRef.current?.closest('.Slide') as HTMLElement | null;
    if (!slideEl) return;

    if (documentMaxWidth && choicesWrapperRef.current) {
      const choicesWidth = choicesWrapperRef.current.offsetWidth;
      const totalMaxWidth = documentMaxWidth + choicesWidth;
      slideEl.style.setProperty('--cata-content-max-width', `${totalMaxWidth}px`);
    } else {
      slideEl.style.removeProperty('--cata-content-max-width');
    }

    return () => {
      slideEl.style.removeProperty('--cata-content-max-width');
    };
  }, [documentMaxWidth]);

  // Screen reader announcement for changes in document edits.
  const renderAriaLiveRegion = () => {
    if (!cataSlideHasDocumentEdits(layout, timeline)) {
      return null;
    }

    return (
      <div className="sr-only" aria-live="polite">
        {changeAnnouncement}
      </div>
    );
  };

  const renderCATAChoices = choices.map((choice) => {
    if (!choice._id) {
      return null;
    }

    return (
      <CATAChoice
        activityComplete={!!activityComplete}
        arrayOfSelectedChoices={selected}
        caretClickFunction={handleCaretClick}
        caretExpanded={expandedChoices.includes(choice._id)}
        choice={choice}
        choiceClickFunction={handleChoiceClick}
        editSetPreview={choice.annotatedEdits || ''}
        explanationClickFunction={handleExplanationButtonClick}
        explanationExpanded={expandedExplanations.includes(choice._id)}
        eyeClickFunction={handleEyeClick}
        hasAnswerBase={hasAnswerBase}
        hasInteractiveDocument={interactiveDocument.length > 0}
        isBestEdit={!!isBestEdit}
        key={choice._id}
        layoutIsHorizontal={!!layoutIsHorizontal}
        slideMainType={slideMainType || ''}
        slideSubType={slideSubType || ''}
        supplementalDocState={supplementalDocState}
        windowSize={windowSize}
        hidePreviewCaret={hidePreviewCaret}
      />
    );
  });

  const renderCATAChoicesWrapper = (
    <div
      ref={choicesWrapperRef}
      className={classNames('CATA-choices-wrapper', windowSize, {
        'horizontal-layout': layoutIsHorizontal,
      })}
    >
      <div
        className={classNames('CATAChoices', {
          'horizontal-layout': layoutIsHorizontal,
          complete: activityComplete,
        })}
        role="group"
        aria-label="Select an Option"
      >
        {/* Choices */}
        {renderCATAChoices}
      </div>
    </div>
  );

  if (slideSubType === CataTypes.EditHorizontal) {
    return (
      <div
        className={classNames('CATA', 'horizontal', 'edit', {
          'has-landscape-doc': hasLandscapeDoc,
          'has-portrait-doc': hasPortraitDoc,
          'activity-complete': activityComplete,
          'has-supplemental-doc': !!supplementalDoc,
        })}
      >
        {isWaitingForMaxWidth ? (
          <div className="upper-content-loading" role="status">
            <Spinner animation="border" className="upper-content-spinner" />
          </div>
        ) : null}

        <div
          className="upper-content"
          style={{
            ...(documentMaxWidth ? { maxWidth: documentMaxWidth } : undefined),
            ...(isWaitingForMaxWidth ? { visibility: 'hidden' as const } : undefined),
          }}
        >
          <FeedbackBar windowSize={windowSize} />
          <div className="CATA-content">
            <InteractiveDocumentContent
              expandedExplanations={expandedExplanations}
              activityComplete={activityComplete}
              choices={choices}
              displayedEditId={displayedEditId}
              hasLandscapeDoc={hasLandscapeDoc}
              hasPortraitDoc={hasPortraitDoc}
              interactiveDocument={interactiveDocument}
              isImageDoc={!!isImageDoc}
              layoutIsHorizontal={layoutIsHorizontal}
              newlyExpandedExplanation={newlyExpandedExplanation}
              panelIds={panelIds}
              slideSubType={slideSubType}
              windowSize={windowSize}
              supplementalDoc={supplementalDoc}
              supplementalDocState={supplementalDocState}
              setSupplementalDocState={setSupplementalDocState}
              setNewlyExpandedExplanation={setNewlyExpandedExplanation}
              onComputedMaxWidth={setDocumentMaxWidth}
            />
          </div>
        </div>
        {renderCATAChoicesWrapper}
        {renderAriaLiveRegion()}
      </div>
    );
  }

  return (
    <div
      ref={cataRef}
      className={classNames('CATA', 'vertical', 'second-to-shrink', {
        'has-doc': interactiveDocument.length > 0,
        'has-landscape-doc': hasLandscapeDoc,
        'has-portrait-doc': hasPortraitDoc,
        edit: slideSubType === CataTypes.EditVertical,
      })}
    >
      <FeedbackBar windowSize={windowSize} />

      {isWaitingForMaxWidth ? (
        <div className="upper-content-loading" role="status">
          <Spinner animation="border" className="upper-content-spinner" />
        </div>
      ) : null}

      <div
        className="CATA-content"
        style={{
          ...(isWaitingForMaxWidth ? { visibility: 'hidden' as const } : undefined),
        }}
      >
        <InteractiveDocumentContent
          activityComplete={activityComplete}
          choices={choices}
          displayedEditId={displayedEditId}
          hasLandscapeDoc={hasLandscapeDoc}
          hasPortraitDoc={hasPortraitDoc}
          interactiveDocument={interactiveDocument}
          layoutIsHorizontal={layoutIsHorizontal}
          newlyExpandedExplanation={newlyExpandedExplanation}
          panelIds={panelIds}
          slideSubType={slideSubType}
          windowSize={windowSize}
          supplementalDoc={supplementalDoc}
          supplementalDocState={supplementalDocState}
          setSupplementalDocState={setSupplementalDocState}
          setNewlyExpandedExplanation={setNewlyExpandedExplanation}
          onComputedMaxWidth={setDocumentMaxWidth}
        />

        {renderCATAChoicesWrapper}
      </div>

      {renderAriaLiveRegion()}
    </div>
  );
};

export default CATA;
